/**
 * A KONTÍR-PUSKA — a döntés hasábjában, a jelöltek és a Tensoft-kontír alatt
 * (#68, a „B" elrendezés; korábban a `MunkalistaC` középső hasábjában).
 *
 * „Tételsorok · 1 sor · 679 000 / 679 000", alatta a sorok: főkönyvi szám,
 * összeg, és a FIRKÁLÁS — a könyvelő szabad jegyzete („ELTE honlap, havi
 * support").
 *
 * ⚠️ A FIRKÁLÁS KIZÁRÓLAG BELSŐ (2.6/15). Ez a komponens az admin felületéé;
 * a garancia viszont nem itt van, hanem a szerveren: a `CLIENT` szerep ehhez
 * a modulhoz nem kap végpontot. Ha a garancia egy itteni feltételen állna, az
 * első „gyorsan mutassuk meg az ügyfélnek is" alatt elveszne.
 *
 * A MÉRCE MUTAT, NEM TILT. A megosztás közben az összeg szükségszerűen nem
 * stimmel — egy „nem menthető, amíg nem egyezik" szabály pont a gondolkodás
 * közben akadna meg. A felület kiírja a különbséget, a döntés a könyvelőé.
 *
 * NINCS „ÚJ SOR" GOMB. A lista végén mindig áll egy üres sor, amibe a
 * könyvelő `Tab`-bal beleér — billentyűzet-első (D9, 2.6/16). A teljesen
 * üres sor nem megy ki a szervernek.
 *
 * ⚠️ CSAK JEGYZET, AMÍG NINCS MÁS KÉRVE *(#68, Viktor 2026-10-19 — a „B”
 * elrendezés)*. A főkönyvi szám, az összeg és a mérce REJTVE van, nem
 * törölve: „ha szükség lesz rá, berakjuk". Az adat a szerveren marad, és
 * nem is íródik felül — a `planWrites` csak a VÁLTOZOTT mezőt küldi, egy
 * rejtett mező pedig nem változhat. Visszakapcsolni az `accounting` propon
 * lehet.
 */

import { useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formatAmount } from '@/i18n/generated';
import type { PaperNotesState } from '../usePaperNotes';
import { isAmountShaped, isFilled, noteFieldId } from './sheet';
import type { DraftRow } from './types';
import type { BookingProposalsState } from './useBookingProposals';

export function BookingSheet({
  state,
  notes,
  accounting = false,
}: {
  state: BookingProposalsState;
  /** A papírra tűzött jegyzetek (#64): a sor jelvénye és a kölcsönös kiemelés. */
  notes?: PaperNotesState;
  /** A főkönyvi szám, az összeg és a mérce — alapból rejtve (#68). */
  accounting?: boolean;
}) {
  const { t } = useTranslation();
  const focusRequest = notes?.focusRequest ?? null;
  const status = state.status;

  // A KÉRÉS OBJEKTUM, nem kulcs: két `E` ugyanarra a sorra kétszer fókuszál.
  // A mező a letétel UTÁNI renderben jelenik meg — ezért effekt, nem hívás.
  useEffect(() => {
    if (focusRequest !== null && status === 'ready') {
      document.getElementById(noteFieldId(focusRequest.key))?.focus();
    }
  }, [focusRequest, status]);

  if (state.status === 'loading') {
    return <p className="hint">{t('booking.loading')}</p>;
  }

  if (state.status === 'failed') {
    return (
      <p className="error">
        {t('booking.failed')}{' '}
        <button type="button" onClick={state.reload}>
          {t('worklist.retry')}
        </button>
      </p>
    );
  }

  const filled = state.rows.filter(isFilled).length;

  return (
    <section
      className={accounting ? 'booking' : 'booking booking--notes'}
      aria-label={t('booking.heading')}
    >
      <header className="booking__head">
        <h3>{t('booking.heading')}</h3>
        <span className="booking__count">{t('booking.rowCount', { count: filled })}</span>
        {accounting ? <Tally state={state} /> : null}
      </header>

      <ul className="booking__rows">
        {state.rows.map((row) => {
          const pinNumber = state.pinNumbers.get(row.key);
          const active =
            notes !== undefined && notes.activeKey === row.key && pinNumber !== undefined;
          // A SOR FÓKUSZA KIEMELI A JELÖLŐJÉT a papíron — a kölcsönösség másik
          // fele. A TŰZETLEN sor fókusza a kiemelést leveszi: egy láthatatlan
          // kiemelés a papír `Esc`-jét nyelné el, látható hatás nélkül.
          // A TŰZETLEN sorra lépve az ő „+"-a kerül a papír sarkába (#68).
          const select =
            notes === undefined
              ? undefined
              : () => {
                  notes.select(pinNumber === undefined ? null : row.key);
                  if (pinNumber === undefined) {
                    notes.adopt(row.key);
                  }
                };

          return (
            <li
              key={row.key}
              className={active ? 'booking__row booking__row--active' : 'booking__row'}
            >
              <RowBadge row={row} pinNumber={pinNumber} notes={notes} />
              {accounting ? <AccountingFields row={row} state={state} onFocus={select} /> : null}
              {/* A FIRKÁLÁS KAPJA A HELYET. Az artboardon is ez a leghosszabb
                  mező: a főkönyvi szám négy jegy, az összeg egy szám, a
                  jegyzet viszont mondat. */}
              <NoteField
                id={noteFieldId(row.key)}
                value={row.note}
                aria-label={t('booking.note')}
                placeholder={t('booking.notePlaceholder')}
                onValue={(value) => state.setCell(row.key, 'note', value)}
                onFocus={select}
                onBlur={() => {
                  state.commit();
                  notes?.settle(row.key);
                }}
                onKeyDown={(event) => noteKey(event, row, state, notes)}
              />
              {/* A ZÁRÓ ÜRES SORT NEM LEHET TÖRÖLNI: nincs mit, és a gomb
                  ott csak egy fölösleges `Tab`-állomás volna. */}
              {isFilled(row) ? (
                <button
                  type="button"
                  className="booking__remove"
                  // A NÉV AZ, AMIT A KÖNYVELŐ LÁT: rejtett főkönyvi számot a
                  // felolvasó se mondjon (#68).
                  aria-label={t('booking.removeRow', {
                    name:
                      (accounting ? row.accountCode.trim() : '') ||
                      row.note.trim() ||
                      t('booking.unnamedRow'),
                  })}
                  onClick={() => state.removeRow(row.key)}
                >
                  ×
                </button>
              ) : (
                <span className="booking__remove booking__remove--empty" aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ul>

      <SaveState saving={state.saving} />
    </section>
  );
}

/**
 * A SOR JELVÉNYE.
 *
 * A JELÖLŐ SZÁMA, nem a sor sorszáma: a Tételsorok sorrendje független a
 * papírtól (Viktor, 10-16). A helye a tűzetlen sorban is megvan, különben a
 * mezők elcsúsznának.
 *
 * A HELY NÉLKÜLI JEGYZET „+"-a (#68): szaggatott, SZÁM NÉLKÜL. A szám a
 * helyből jön — egy sarokba tett jelölő 1-esként mindent átszámozna. A gomb
 * a papíron indítja a letételt.
 */
function RowBadge({
  row,
  pinNumber,
  notes,
}: {
  row: DraftRow;
  pinNumber: number | undefined;
  notes: PaperNotesState | undefined;
}) {
  const { t } = useTranslation();

  if (pinNumber !== undefined) {
    return (
      <span
        className="booking__pin"
        role="img"
        aria-label={t('booking.pinBadge', { number: pinNumber })}
      >
        {pinNumber}
      </span>
    );
  }
  if (notes !== undefined && row.note.trim() !== '') {
    return (
      <button
        type="button"
        className="booking__pin booking__pin--loose"
        aria-label={t('booking.pinLoose', { label: row.note.trim() })}
        onClick={() => notes.placeLoose(row.key)}
      >
        +
      </button>
    );
  }
  return <span className="booking__pin booking__pin--none" aria-hidden="true" />;
}

/** A FŐKÖNYVI SZÁM ÉS AZ ÖSSZEG — csak `accounting` mellett (#68). */
function AccountingFields({
  row,
  state,
  onFocus,
}: {
  row: DraftRow;
  state: BookingProposalsState;
  onFocus: (() => void) | undefined;
}) {
  const { t } = useTranslation();
  // A ROSSZ ALAKÚ ÖSSZEG HELYBEN SZÓL. A szerver is elutasítaná, és
  // meg is nevezné — de csak mentéskor, amikor a könyvelő már
  // továbblépett a mezőről.
  const badAmount = row.amount.trim().length > 0 && !isAmountShaped(row.amount);

  return (
    <>
      <input
        className="booking__account"
        value={row.accountCode}
        aria-label={t('booking.accountCode')}
        placeholder={t('booking.accountCode')}
        onChange={(event) => state.setCell(row.key, 'accountCode', event.target.value)}
        onFocus={onFocus}
        onBlur={state.commit}
        onKeyDown={(event) => commitOnEnter(event, state.commit)}
      />
      <input
        className={badAmount ? 'booking__amount booking__amount--bad' : 'booking__amount'}
        value={row.amount}
        inputMode="decimal"
        aria-label={t('booking.amount')}
        aria-invalid={badAmount || undefined}
        placeholder={t('booking.amount')}
        onChange={(event) => state.setCell(row.key, 'amount', event.target.value)}
        onFocus={onFocus}
        onBlur={state.commit}
        onKeyDown={(event) => commitOnEnter(event, state.commit)}
      />
    </>
  );
}

/**
 * A JEGYZET MEZŐJE — `<textarea>`, és a TELJES szöveg látszik (#68).
 *
 * Egy sorba vágott `<input>`-ban a hosszú jegyzet vége a mező mögé csúszott,
 * és csak a kurzorral volt elolvasható. Itt a mező a szöveggel nő.
 *
 * ⚠️ A MAGASSÁG KÉZZEL MÉRVE, nem `field-sizing: content`-tel: az a Firefoxban
 * nincs meg. A mérés a szöveggel ÉS a szélességgel együtt fut — keskenyebb
 * hasábban ugyanaz a mondat több sorba tör.
 *
 * ⚠️ A JEGYZET EGY SOR — A TÖRDELÉS CSAK MEGJELENÍTÉS *(Viktor, 2026-10-19)*.
 * Az `Enter` itt sem új sor, hanem „kész" (`commitOnEnter`); a BEILLESZTETT
 * szöveg sortörése szóköz lesz. A régi `<input>` ezt magától tette, a
 * `<textarea>` nem — enélkül egy e-mailből másolt bekezdés sortörésekkel
 * mentődne. A beillesztést KÜLÖN kezeljük, nem csak az `onChange`-ben: ott a
 * csere után a vezérelt mező a kurzort a szöveg végére ugratná.
 */
function NoteField({
  onValue,
  ...props
}: Omit<React.ComponentProps<'textarea'>, 'value' | 'onChange'> & {
  value: string;
  onValue: (value: string) => void;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);
  const { value } = props;

  // biome-ignore lint/correctness/useExhaustiveDependencies: a szöveg változása ÚJRAMÉRÉST kér, akkor is, ha a törzs nem olvassa
  useLayoutEffect(() => {
    const field = ref.current;
    if (field === null) {
      return;
    }
    const measure = () => {
      field.style.height = 'auto';
      // A `scrollHeight` a keretet nem tartalmazza, a `border-box` magasság igen.
      field.style.height = `${field.scrollHeight + field.offsetHeight - field.clientHeight}px`;
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    let width = field.clientWidth;
    const observer = new ResizeObserver(() => {
      // CSAK A SZÉLESSÉG számít: a saját magasság-állításunk is ide fut be,
      // és arra újra mérni végtelen kör volna.
      if (field.clientWidth !== width) {
        width = field.clientWidth;
        measure();
      }
    });
    observer.observe(field);
    return () => observer.disconnect();
  }, [value]);

  return (
    <textarea
      ref={ref}
      className="booking__note"
      rows={1}
      {...props}
      onChange={(event) => onValue(oneLine(event.target.value))}
      onPaste={(event) => {
        const text = event.clipboardData.getData('text');
        if (!/[\r\n]/.test(text)) {
          return;
        }
        event.preventDefault();
        const field = event.currentTarget;
        field.setRangeText(oneLine(text), field.selectionStart, field.selectionEnd, 'end');
        onValue(field.value);
      }}
    />
  );
}

/** A sortörés — a körülötte álló szóközökkel együtt — EGY szóköz. */
function oneLine(text: string): string {
  return text.replace(/\s*[\r\n]+\s*/g, ' ');
}

/**
 * A MÉRCE — „679 000 / 679 000".
 *
 * HÁROM ÁLLAPOT, nem kettő. Az „egyezik" és a „nem egyezik" mellett ott a
 * harmadik: amikor a bizonylatnak MÉG NINCS bruttója (a mezői a lefűzéskor
 * íródnak rá). Akkor nincs mihez mérni, és egy „nem egyezik" jelzés ott
 * hibát állítana ott, ahol csak adat hiányzik.
 */
function Tally({ state }: { state: BookingProposalsState }) {
  const { t } = useTranslation();

  if (state.allocated === null) {
    return (
      <span className="booking__tally booking__tally--unknown">{t('booking.tallyUnknown')}</span>
    );
  }

  const allocated = formatAmount(Number(state.allocated));

  if (state.grossAmount === null) {
    return <span className="booking__tally">{allocated}</span>;
  }

  // SZÖVEGKÉNT hasonlítunk, nem számként: mindkét oldal a szerver
  // `toFixed(2)`-jének alakjában áll, tehát az egyezés karakterre pontos —
  // és nem kell `number`-be vinni a pénzt csak azért, hogy összevessük.
  const matches = state.allocated === state.grossAmount;

  return (
    <span className={matches ? 'booking__tally booking__tally--match' : 'booking__tally'}>
      {allocated} / {formatAmount(Number(state.grossAmount))}
    </span>
  );
}

/**
 * A MENTÉS NYUGTÁJA. A `saving` ág szándékosan NÉMA: egy pár száz
 * ezredmásodperces „Mentés…" felirat minden mezőelhagyásnál felvillanna, és
 * a könyvelő megtanulná átnézni rajta — pont akkor, amikor a `failed`-et is
 * ott kellene meglátnia.
 */
function SaveState({ saving }: { saving: BookingProposalsState['saving'] }) {
  const { t } = useTranslation();

  switch (saving) {
    case 'saved':
      return <p className="booking__saved">{t('booking.saved')}</p>;
    case 'failed':
      return (
        <p className="error" role="alert">
          {t('booking.saveFailed')}
        </p>
      );
    default:
      return null;
  }
}

/**
 * A JEGYZET MEZŐJE — a papírról ide érkezik a fókusz (`N`, `E`), és innen
 * tér vissza: a TŰZÖTT sorban az `Enter` ment ÉS visszavisz a papírra, hogy
 * a következő `N` ott folytatódjon; az `Esc` mégse — a szöveg nélküli új
 * jelölőt elveti, a többinél csak visszavisz (a `blur` ment).
 */
function noteKey(
  event: React.KeyboardEvent,
  row: DraftRow,
  state: BookingProposalsState,
  notes: PaperNotesState | undefined
) {
  if (notes !== undefined && row.pin !== null && event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    notes.leave(row);
    return;
  }
  commitOnEnter(event, state.commit);
  if (notes !== undefined && row.pin !== null && event.key === 'Enter') {
    notes.paper.current?.focus();
  }
}

/**
 * AZ `Enter` A PUSKÁBAN MENT, nem lép tovább.
 *
 * A `useShortcuts` a beviteli mezőket kihagyja, tehát a sor-mód `Enter`-e
 * itt amúgy sem sülne el — de a semmi is rossz válasz: a billentyűzet-első
 * munkában az `Enter` „kész vagyok ezzel"-t jelent. A `stopPropagation`
 * azért van ott, hogy ez a jelentés akkor is megmaradjon, ha később valaki
 * a mezőkre is kiterjeszti a rövidítéseket.
 */
function commitOnEnter(event: React.KeyboardEvent, commit: () => void) {
  if (event.key === 'Enter') {
    event.preventDefault();
    event.stopPropagation();
    commit();
  }
}
